import React, { useEffect, useState } from "react";

/**
 * Live "Just shipped to [City]" rotating ticker.
 * Rotates every 3.5s. Tries to detect visitor's city via geo-IP on mount,
 * shows it first if successful. Otherwise rotates the default list of
 * USA / EU / AU cities (the ad-target markets).
 */

const FLAG_BY_CC = {
  US: "🇺🇸",
  GB: "🇬🇧",
  FR: "🇫🇷",
  DE: "🇩🇪",
  IT: "🇮🇹",
  ES: "🇪🇸",
  NL: "🇳🇱",
  AU: "🇦🇺",
  NZ: "🇳🇿",
  CA: "🇨🇦",
  IE: "🇮🇪",
  BE: "🇧🇪",
  PT: "🇵🇹",
  CH: "🇨🇭",
  AT: "🇦🇹",
  SE: "🇸🇪",
  NO: "🇳🇴",
  DK: "🇩🇰",
  FI: "🇫🇮",
  PL: "🇵🇱",
  MX: "🇲🇽",
  AR: "🇦🇷",
  BR: "🇧🇷",
  CL: "🇨🇱",
  CO: "🇨🇴",
  PE: "🇵🇪",
  AE: "🇦🇪",
  JP: "🇯🇵",
  SG: "🇸🇬",
};

const DEFAULT_CITIES = [
  { city: "Brooklyn, NY", flag: "🇺🇸" },
  { city: "Los Angeles, CA", flag: "🇺🇸" },
  { city: "Miami, FL", flag: "🇺🇸" },
  { city: "Austin, TX", flag: "🇺🇸" },
  { city: "Chicago, IL", flag: "🇺🇸" },
  { city: "London, UK", flag: "🇬🇧" },
  { city: "Manchester, UK", flag: "🇬🇧" },
  { city: "Paris, FR", flag: "🇫🇷" },
  { city: "Berlin, DE", flag: "🇩🇪" },
  { city: "Milan, IT", flag: "🇮🇹" },
  { city: "Barcelona, ES", flag: "🇪🇸" },
  { city: "Amsterdam, NL", flag: "🇳🇱" },
  { city: "Sydney, AU", flag: "🇦🇺" },
  { city: "Melbourne, AU", flag: "🇦🇺" },
  { city: "Auckland, NZ", flag: "🇳🇿" },
  { city: "Toronto, CA", flag: "🇨🇦" },
];

export default function ShippedTicker() {
  const [cities, setCities] = useState(DEFAULT_CITIES);
  const [idx, setIdx] = useState(0);

  // Geo-IP — prepend visitor's city if detectable.
  useEffect(() => {
    let cancelled = false;
    const cacheKey = "dp_geo_v1";
    const tryApply = (geo) => {
      if (cancelled || !geo?.city) return;
      const flag = FLAG_BY_CC[geo.country_code] || "📍";
      const localized = {
        city: `${geo.city}${geo.region_code ? `, ${geo.region_code}` : ""}`,
        flag,
        local: true,
      };
      setCities((prev) => [localized, ...prev.filter((c) => c.city !== localized.city)]);
      setIdx(0);
    };

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        tryApply(JSON.parse(cached));
        return;
      } catch {
        /* fall through */
      }
    }

    fetch("https://ipapi.co/json/", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || data.error || !data.city) return;
        const minimal = {
          city: data.city,
          region_code: data.region_code || "",
          country_code: data.country_code || "",
        };
        try {
          localStorage.setItem(cacheKey, JSON.stringify(minimal));
        } catch {
          /* ignore quota */
        }
        tryApply(minimal);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % cities.length);
    }, 3500);
    return () => clearInterval(t);
  }, [cities.length]);

  const current = cities[idx];

  return (
    <div
      data-testid="shipped-ticker"
      className="bg-white border-y border-black/5 py-2.5"
    >
      <div className="container-x flex items-center justify-center gap-3 text-[11px] md:text-[12px] font-semibold text-[#0e0e10] overflow-hidden">
        <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] pulse-dot shrink-0" />
        <span className="text-[#6b6b72] uppercase tracking-[0.18em] text-[10px] font-extrabold shrink-0">
          Live
        </span>
        <span className="opacity-30 hidden md:inline">·</span>
        <div
          key={idx}
          className="fade-in-up inline-flex items-center gap-1.5 truncate"
        >
          <span className="text-[14px] shrink-0">{current.flag}</span>
          <span>{current.local ? "Just shipped — close to" : "Just shipped to"}</span>
          <strong className="text-[#0e0e10]">{current.city}</strong>
          {current.local && (
            <span className="hidden md:inline text-[10px] uppercase tracking-wider text-[#22c55e] font-extrabold ml-1">
              near you
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
